import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SourcePicker } from "@/components/SourcePicker";
import {
  Loader2, ShieldCheck, User, Type, AlertTriangle, Trash2, Check, Search, X,
  Pencil, ChevronUp, ChevronDown, Plus, Link2,
} from "lucide-react";

// Feature 34 (Final Integration) — canonical statement editor. The letter body IS these ordered,
// classified statements. Substantive text stays unconfirmed and blocks export until the tenant
// links a source (source-supported) or confirms it as their own statement.
const CLASS_META = {
  source_supported: { label: "Source-supported", icon: ShieldCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  user_statement: { label: "Your statement", icon: User, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  neutral_template: { label: "Neutral wording", icon: Type, cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const REASON_COPY = {
  source_supported_without_source: "Link a valid source, or reclassify as your own statement.",
  citation_invalid_deleted_or_foreign: "The cited record was deleted or is unavailable.",
  user_statement_awaiting_confirmation: "Confirm this as your own statement to include it.",
  ai_supported_statement_awaiting_confirmation: "Confirm this AI-drafted supported statement.",
  neutral_template_must_not_cite_sources: "Neutral wording can't cite a source.",
  neutral_template_not_allowlisted: "Only reviewed neutral wording can be marked neutral.",
  unclassified_statement: "Classify this statement before export.",
};

// Strip resolver-added fields so we re-send only the citation ref shape the API expects.
function stripRef(r) {
  const { record_type, record_id, charge_index, page, excerpt } = r;
  const out = { record_type, record_id };
  if (charge_index !== undefined && charge_index !== null) out.charge_index = charge_index;
  if (page) out.page = page;
  if (excerpt) out.excerpt = excerpt;
  return out;
}

export function EvidenceCitedStatements({ letterId, onChange }) {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [inspect, setInspect] = useState(null);
  const [picker, setPicker] = useState(null); // { mode: 'add' | statementId }
  const [showAdd, setShowAdd] = useState(false);
  const [text, setText] = useState("");
  const [cls, setCls] = useState("user_statement");
  const [addRefs, setAddRefs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dispute-letters/id/${letterId}/statements`);
      setStatements(data.filter((s) => s.status === "active"));
    } catch { /* letter may not support statements */ } finally { setLoading(false); }
  }, [letterId]);

  useEffect(() => { load(); }, [load]);
  const refresh = async () => { await load(); onChange && onChange(); };

  const add = async () => {
    if (!text.trim()) { toast.error("Enter the statement text."); return; }
    setBusy(true);
    try {
      await api.post(`/dispute-letters/id/${letterId}/statements`, {
        text: text.trim(), classification: cls,
        source_refs: cls === "source_supported" ? addRefs : [],
      });
      setText(""); setAddRefs([]); setCls("user_statement"); setShowAdd(false);
      await refresh();
      toast.success("Statement added.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't add that statement.");
    } finally { setBusy(false); }
  };

  const patch = async (s, body) => {
    try { await api.put(`/statements/${s.statement_id}`, body); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't update."); }
  };
  const saveEdit = async (s) => {
    if (!editText.trim()) { toast.error("Statement can't be empty."); return; }
    await patch(s, { text: editText.trim() });
    setEditId(null); setEditText("");
  };
  const reclassify = (s, classification) => patch(s, { classification });
  const confirm = (s) => patch(s, { confirmation_status: "confirmed" });
  const remove = async (s) => {
    try { await api.delete(`/statements/${s.statement_id}`); await refresh(); }
    catch { toast.error("Couldn't remove."); }
  };
  const openInspect = async (s) => {
    try { const { data } = await api.get(`/statements/${s.statement_id}/citations`); setInspect(data); }
    catch { toast.error("Couldn't load citation details."); }
  };

  const onPickSource = async (ref) => {
    const target = picker;
    setPicker(null);
    if (target?.mode === "add") { setAddRefs([ref]); setCls("source_supported"); return; }
    const s = statements.find((x) => x.statement_id === target?.mode);
    if (s) await patch(s, { classification: "source_supported", source_refs: [...(s.source_refs || []).map(stripRef), ref] });
  };
  const unlink = (s, i) => patch(s, { source_refs: (s.source_refs || []).map(stripRef).filter((_, idx) => idx !== i) });

  const move = async (idx, dir) => {
    const arr = [...statements];
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= arr.length) return;
    if (arr[j].block_index !== arr[idx].block_index) return; // keep paragraphs coherent
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.post(`/dispute-letters/id/${letterId}/statements/reorder`, { ordered_ids: arr.map((s) => s.statement_id) }); await refresh(); }
    catch { toast.error("Couldn't reorder."); }
  };

  const blocking = statements.filter((s) => !s.export_ready);

  return (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="cited-statements-panel">
      <h3 className="font-heading text-base font-semibold text-navy">Letter statements</h3>
      <p className="text-xs text-[#64748B] mt-1">Your letter is built from these statements. Each factual sentence must be confirmed as your own or backed by a source before you can export.</p>

      <div className={`mt-3 rounded-xl border px-3 py-2 text-sm ${blocking.length ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`} data-testid="cited-readiness">
        {blocking.length
          ? <span className="flex items-center gap-2"><AlertTriangle size={15} /> {blocking.length} statement{blocking.length > 1 ? "s" : ""} need attention before export.</span>
          : <span className="flex items-center gap-2"><Check size={15} /> All statements are supported or confirmed.</span>}
      </div>

      {loading ? <div className="mt-4 flex justify-center"><Loader2 className="animate-spin text-[#94A3B8]" /></div> : (
        <div className="mt-3 space-y-2" data-testid="cited-statements-list">
          {statements.map((s, idx) => {
            if (s.is_formatting) {
              return (
                <div key={s.statement_id} data-testid={`stmt-fmt-${s.statement_id}`} className="rounded-lg bg-[#F8FAFC] border border-dashed border-[#E2E8F0] px-3 py-2">
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-[#94A3B8]">{(s.block_kind || "format").replace("_", " ")}</span>
                  <p className="text-xs text-[#64748B] whitespace-pre-line">{s.text}</p>
                </div>
              );
            }
            const meta = CLASS_META[s.classification] || CLASS_META.neutral_template;
            const editing = editId === s.statement_id;
            const canUp = idx > 0 && statements[idx - 1].block_index === s.block_index;
            const canDown = idx < statements.length - 1 && statements[idx + 1].block_index === s.block_index;
            return (
              <div key={s.statement_id} data-testid={`stmt-${s.statement_id}`} className={`rounded-xl border p-3 ${s.export_ready ? "border-[#E2E8F0]" : "border-amber-300 bg-amber-50/40"}`}>
                <div className="flex items-start gap-2">
                  <div className="flex flex-col shrink-0 -mt-0.5">
                    <button data-testid={`stmt-up-${s.statement_id}`} disabled={!canUp} onClick={() => move(idx, "up")} className="text-[#CBD5E1] disabled:opacity-30 hover:text-[#64748B]"><ChevronUp size={14} /></button>
                    <button data-testid={`stmt-down-${s.statement_id}`} disabled={!canDown} onClick={() => move(idx, "down")} className="text-[#CBD5E1] disabled:opacity-30 hover:text-[#64748B]"><ChevronDown size={14} /></button>
                  </div>
                  <div className="min-w-0 flex-1">
                    {editing ? (
                      <div className="space-y-2">
                        <textarea data-testid={`stmt-edit-text-${s.statement_id}`} value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="w-full text-sm border border-[#E2E8F0] rounded-lg p-2" />
                        <div className="flex gap-2">
                          <Button data-testid={`stmt-edit-save-${s.statement_id}`} size="sm" className="h-7 rounded-lg bg-navy text-white text-xs" onClick={() => saveEdit(s)}>Save</Button>
                          <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => { setEditId(null); setEditText(""); }}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[#1E293B]">{s.text}</p>
                    )}
                  </div>
                  {!editing && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button data-testid={`stmt-editbtn-${s.statement_id}`} onClick={() => { setEditId(s.statement_id); setEditText(s.text); }} className="text-[#94A3B8] hover:text-navy"><Pencil size={14} /></button>
                      <button data-testid={`stmt-remove-${s.statement_id}`} onClick={() => remove(s)} className="text-[#94A3B8] hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>

                {!editing && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <select data-testid={`stmt-class-${s.statement_id}`} value={s.classification} onChange={(e) => reclassify(s, e.target.value)}
                      className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-1 ${meta.cls}`}>
                      <option value="source_supported">Source-supported</option>
                      <option value="user_statement">Your statement</option>
                      <option value="neutral_template">Neutral wording</option>
                    </select>
                    {s.provenance === "ai_generated" && <span className="text-[10px] text-[#64748B]">AI-drafted</span>}

                    {s.classification === "source_supported" && (s.source_refs || []).map((r, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-700 rounded px-1.5 py-0.5">
                        {r.snapshot?.summary || r.record_type} <button onClick={() => unlink(s, i)} className="hover:text-red-600"><X size={9} /></button>
                      </span>
                    ))}
                    {s.classification === "source_supported" && (
                      <button data-testid={`stmt-linksource-${s.statement_id}`} onClick={() => setPicker({ mode: s.statement_id })} className="inline-flex items-center gap-1 text-[11px] text-brand font-medium"><Link2 size={11} /> Link source</button>
                    )}
                    {s.classification === "source_supported" && (s.source_refs || []).length > 0 && (
                      <button data-testid={`stmt-inspect-${s.statement_id}`} onClick={() => openInspect(s)} className="inline-flex items-center gap-1 text-[11px] text-[#4338CA] font-medium"><Search size={11} /> View</button>
                    )}
                    {s.classification === "user_statement" && s.confirmation_status !== "confirmed" && (
                      <Button data-testid={`stmt-confirm-${s.statement_id}`} size="sm" variant="outline" className="h-6 rounded-lg text-[11px]" onClick={() => confirm(s)}>Confirm as mine</Button>
                    )}
                    {s.classification === "source_supported" && s.provenance === "ai_generated" && s.confirmation_status !== "confirmed" && (s.source_refs || []).length > 0 && (
                      <Button data-testid={`stmt-confirm-${s.statement_id}`} size="sm" variant="outline" className="h-6 rounded-lg text-[11px]" onClick={() => confirm(s)}>Confirm</Button>
                    )}
                    {s.classification === "user_statement" && s.confirmation_status === "confirmed" && (
                      <span className="text-[10px] text-[#64748B]">Confirmed by you — not independently verified.</span>
                    )}
                  </div>
                )}
                {!s.export_ready && !editing && <p data-testid={`stmt-block-${s.statement_id}`} className="mt-1.5 text-[11px] text-amber-700">{REASON_COPY[s.blocking_reason] || "Needs attention before export."}</p>}
              </div>
            );
          })}
        </div>
      )}

      {showAdd ? (
        <div className="mt-4 rounded-xl border border-dashed border-[#CBD5E1] p-3 space-y-2" data-testid="cited-add-form">
          <textarea data-testid="cited-add-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a statement…" className="w-full text-sm border border-[#E2E8F0] rounded-lg p-2" />
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="cited-add-class" value={cls} onChange={(e) => setCls(e.target.value)} className="text-xs border border-[#E2E8F0] rounded-lg p-1.5">
              <option value="source_supported">Source-supported fact</option>
              <option value="user_statement">Your statement</option>
              <option value="neutral_template">Neutral wording</option>
            </select>
            {cls === "source_supported" && (
              <button data-testid="cited-add-linksource" onClick={() => setPicker({ mode: "add" })} className="inline-flex items-center gap-1 text-[11px] text-brand font-medium border border-[#E2E8F0] rounded-lg px-2 py-1.5"><Link2 size={12} /> {addRefs.length ? "Change source" : "Link source"}</button>
            )}
            {addRefs.map((r, i) => <span key={i} className="text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-700 rounded px-1.5 py-0.5">{r.record_type}</span>)}
            <Button data-testid="cited-add-btn" size="sm" disabled={busy} onClick={add} className="rounded-lg text-xs bg-navy text-white h-8 ml-auto">{busy ? <Loader2 size={13} className="animate-spin" /> : "Add"}</Button>
            <Button size="sm" variant="ghost" className="rounded-lg text-xs h-8" onClick={() => { setShowAdd(false); setText(""); setAddRefs([]); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button data-testid="cited-add-open" variant="outline" size="sm" className="mt-4 rounded-lg text-xs" onClick={() => setShowAdd(true)}><Plus size={13} className="mr-1" /> Add statement</Button>
      )}

      <SourcePicker letterId={letterId} open={!!picker} onClose={() => setPicker(null)} onPick={onPickSource} />

      {inspect && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" data-testid="cited-inspect-modal" onClick={() => setInspect(null)}>
          <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h4 className="font-semibold text-navy text-sm">Cited source</h4><button onClick={() => setInspect(null)}><X size={16} /></button></div>
            <p className="text-sm text-[#334155]">{inspect.text}</p>
            {(inspect.citations || []).map((c, i) => (
              <div key={i} data-testid={`inspect-citation-${i}`} className={`rounded-xl border p-3 text-xs ${c.valid ? "border-[#E2E8F0]" : "border-red-200 bg-red-50"}`}>
                {c.valid ? (
                  <>
                    <div className="font-semibold text-[#1E293B]">{c.snapshot.summary}</div>
                    <div className="text-[#64748B] mt-1">Type: {c.snapshot.record_type} · {c.snapshot.source_class}</div>
                    {c.snapshot.date && <div className="text-[#64748B]">Date ({c.snapshot.date_label}): {c.snapshot.date}</div>}
                    <div className="text-[#64748B]">State: {c.snapshot.seal_state}</div>
                    {c.snapshot.page && <div className="text-[#64748B]">Page {c.snapshot.page}{c.snapshot.excerpt ? ` — "${c.snapshot.excerpt}"` : ""}</div>}
                  </>
                ) : <div className="text-red-700 flex items-center gap-1.5"><AlertTriangle size={13} /> {c.frozen_snapshot ? "Live source unavailable — showing the record as cited when this letter was used." : `Source unavailable (${c.reason}). Link a valid record or reclassify.`}</div>}
                {!c.valid && c.frozen_snapshot && (
                  <div className="mt-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-2" data-testid={`inspect-frozen-${i}`}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-[#94A3B8]">Frozen at time of use</div>
                    <div className="font-semibold text-[#1E293B] mt-0.5">{c.frozen_snapshot.summary}</div>
                    <div className="text-[#64748B]">Type: {c.frozen_snapshot.record_type} · {c.frozen_snapshot.source_class}</div>
                    {c.frozen_snapshot.date && <div className="text-[#64748B]">Date ({c.frozen_snapshot.date_label}): {c.frozen_snapshot.date}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
